/**
 * Hook for displaying error toast notifications
 * @module hooks/useErrorToast
 */

import { toast } from 'sonner'
import { categorizeError, getUserFriendlyMessage } from '@/utils/error-handler'

/**
 * Hook providing toast notification utilities for error handling
 *
 * @returns Object with toast display functions
 *
 * @example
 * ```typescript
 * const { showError, showSuccess } = useErrorToast()
 *
 * try {
 *   await fetchEmployees()
 *   showSuccess('Employees loaded successfully')
 * } catch (error) {
 *   showError(error)
 * }
 * ```
 */
export const useErrorToast = () => {
  /**
   * Display error toast with user-friendly message
   * @param error - Error object to display
   * @param locale - Locale for error message (default: pt-BR)
   */
  const showError = (error: unknown, locale: 'en' | 'pt-BR' = 'pt-BR') => {
    const message = getUserFriendlyMessage(error, locale)
    const categorized = categorizeError(error)

    toast.error(message, {
      description:
        categorized.category !== 'unknown'
          ? undefined
          : 'Código do erro: ERR_UNKNOWN',
      duration: 5000,
      action: categorized.isRetriable
        ? {
            label: locale === 'pt-BR' ? 'Tentar novamente' : 'Retry',
            onClick: () => {
              // Retry action will be provided by component
            }
          }
        : undefined
    })

    return categorized
  }

  /**
   * Display success toast
   * @param message - Success message to display
   * @param locale - Locale for message (affects auto-generated descriptions)
   */
  const showSuccess = (
    message: string,
    options?: { description?: string }
  ) => {
    toast.success(message, {
      description: options?.description,
      duration: 5000
    })
  }

  /**
   * Display info toast
   * @param message - Info message to display
   */
  const showInfo = (message: string, options?: { description?: string }) => {
    toast.info(message, {
      description: options?.description,
      duration: 5000
    })
  }

  /**
   * Display warning toast
   * @param message - Warning message to display
   */
  const showWarning = (message: string, options?: { description?: string }) => {
    toast.warning(message, {
      description: options?.description,
      duration: 5000
    })
  }

  /**
   * Display loading toast (manual dismiss)
   * @param message - Loading message to display
   * @returns Toast ID for dismissal
   */
  const showLoading = (message: string) => {
    return toast.loading(message)
  }

  /**
   * Dismiss a specific toast
   * @param toastId - ID of the toast to dismiss
   */
  const dismiss = (toastId: string | number) => {
    toast.dismiss(toastId)
  }

  /**
   * Dismiss all toasts
   */
  const dismissAll = () => {
    toast.dismiss()
  }

  return {
    showError,
    showSuccess,
    showInfo,
    showWarning,
    showLoading,
    dismiss,
    dismissAll
  }
}
